<template>
  <VDialog :open="isOpen" :headline="title" modal @close="onCancel" style="--dialog-min-inline-size: 500px; --dialog-max-block-size: 80vh;">
    <template #body>
      <div class="modal-container">
        <div v-if="loading" class="spinner-overlay">
          <VProgressRing connotation="cta" />
        </div>

        <span
          v-if="mode === 'access_token' || mode === 'refresh_token'"
          class="modal-description"
        >
          Set how long your token remains active before it expires.
        </span>

        <div
          v-if="tokenToDisplay || durationError === ''"
          class="warning-message"
        >
          <VIcon :name="warningOutline" />
          <span>{{ warningMessage }}</span>
        </div>

        <div
          v-if="(mode === 'access_token' || mode === 'refresh_token') && !generatedToken"
          class="field"
        >
          <div class="method-url-row">
            <VSelect
              v-model="selectedUnit"
              appearance="ghost"
              class="method-select"
              fixed-dropdown
            >
              <VOption value="days" text="Days" />
              <VOption value="hours" text="Hours" />
              <VOption value="minutes" text="Minutes" />
            </VSelect>

            <VTextField
              v-model="durationValue"
              type="number"
              placeholder="Enter value"
              appearance="ghost"
              class="url-input"
              :class="{ invalid: durationError }"
              @input="validateDuration(true)"
            />
          </div>
          <div v-if="durationError" class="error-text">
            {{ durationError }}
          </div>
        </div>

        <div
          v-if="(mode === 'access_token' || mode === 'refresh_token') && !generatedToken"
          class="expiry-disclaimer"
        >
          <VIcon :name="informationCircleOutline" />
          <span>
            Minimum expiry is <strong>3 minutes</strong> and maximum is
            <strong>30 days</strong>.
          </span>
        </div>

        <!-- Inline API error — toast can't appear above VDialog's top-layer -->
        <div v-if="apiError" class="api-error-banner" role="alert">
          <VIcon name="error-line" :size="-4" connotation="alert" />
          <span>{{ apiError }}</span>
        </div>

        <div v-if="tokenToDisplay" class="token-display">
          <div class="token-wrapper">
            <VTextField
              :type="showToken ? 'text' : 'password'"
              :modelValue="tokenToDisplay"
              readonly
              appearance="ghost"
              class="token-input"
            />

            <div class="token-icons">
              <div class="tooltip">
                <VIcon
                  :name="showToken ? eyeOffOutline : eyeOutline"
                  @click="toggleShowToken"
                  class="token-icon"
                />
                <span class="tooltip-text">{{ showToken ? "Hide" : "Show" }}</span>
              </div>

              <div class="tooltip">
                <VIcon
                  :name="copyOutline"
                  @click="copyToClipboard(tokenToDisplay)"
                  class="token-icon"
                />
                <span class="tooltip-text">Copy</span>
              </div>

              <div class="tooltip">
                <VIcon
                  :name="downloadOutline"
                  @click="downloadToken"
                  class="token-icon"
                />
                <span class="tooltip-text">Download</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #action-items v-if="(mode === 'access_token' || mode === 'refresh_token') && !generatedToken">
      <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" @click="onCancel" />
      <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Save" :disabled="!!durationError" @click="onSave" />
    </template>
  </VDialog>
</template>

<script>
import apiInterceptor from "@/shared/apiInterceptor";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';
import { useClipboard } from '@/composables/useClipboard';

export default {
  name: "TokenExpiryModal",

  setup() {
    const { copyToClipboard } = useClipboard();
    return { copyToClipboard };
  },

  props: {
    isOpen: Boolean,
    keyId: { type: [String, Number, null], default: null },
    mode: {
      type: String,
      default: "access_token",
    },
    clientSecret: { type: String, default: "" },
  },

  emits: ["onSave", "onCancel", "fetchList"],

  data() {
    return {
      loading: false,
      generatedToken: null,
      showToken: false,
      durationValue: "",
      selectedUnit: "days",
      durationError: "",
      apiError: "",

      warningOutline: 'warning-line',
      eyeOutline: 'eye-line',
      eyeOffOutline: 'eye-hide-line',
      copyOutline: 'copy-2-line',
      downloadOutline: 'download-line',
      informationCircleOutline: 'info-line',
    };
  },

  watch: {
    isOpen(val) {
      if (!val) {
        this.resetState();
        return;
      }

      if (this.mode === "access_token" || this.mode === "refresh_token") {
        this.selectedUnit = "days";
        this.durationValue = "15";
      }

      this.durationError = "";
      this.generatedToken = null;
      this.showToken = false;
    },
    selectedUnit() {
      this.validateDuration(true);
    },
    durationValue() {
      this.validateDuration(false);
    },
  },

  computed: {
    tokenToDisplay() {
      return this.mode === "client_secret"
        ? this.clientSecret
        : this.generatedToken;
    },
    title() {
      if (this.generatedToken) {
        if (this.mode === "access_token") return "Access Token View/Copy/Download";
        if (this.mode === "refresh_token") return "Refresh Token View/Copy/Download";
      }
      const map = {
        client_secret: "Manage Client Secret",
        refresh_token: "Manage Refresh Token Expiry Window",
        access_token: "Manage Access Token Expiry Window",
      };
      return map[this.mode] || map.access_token;
    },
    modeLabel() {
      const labelMap = {
        client_secret: "Client secret",
        refresh_token: "Refresh token",
        access_token: "Access token",
      };
      return labelMap[this.mode] || labelMap.access_token;
    },
    warningMessage() {
      const warningMap = {
        client_secret: "Client secret can be viewed, copied, and downloaded only once.",
        refresh_token: "Refreshing the token expires older tokens in 3-5 minutes, and the new token is accessible to view, copy, and download only once.",
        access_token: "Access token can be viewed, copied, and downloaded only once.",
      };
      return warningMap[this.mode] || warningMap.access_token;
    },
    unitMax() {
      if (this.selectedUnit === "days") return 30;
      if (this.selectedUnit === "hours") return 720;
      if (this.selectedUnit === "minutes") return 43200;
      return 0;
    },
    downloadFilename() {
      const fileMap = {
        client_secret: "client_secret.txt",
        refresh_token: "refresh_token.txt",
        access_token: "access_token.txt",
      };
      return fileMap[this.mode] || fileMap.access_token;
    },
  },

  methods: {
    resetState() {
      this.generatedToken = null;
      this.durationError = "";
      this.durationValue = "";
      this.apiError = "";
      this.showToken = false;
    },

    validateDuration(setError = false) {
      if (this.mode !== "access_token" && this.mode !== "refresh_token") return true;

      const raw = this.durationValue;
      const v = Number(raw);

      if (!raw) {
        if (setError) this.durationError = "Value is required.";
        return false;
      }
      if (!Number.isInteger(v)) {
        if (setError) this.durationError = "Value must be an integer.";
        return false;
      }
      if (v <= 0) {
        if (setError) this.durationError = "Value must be greater than zero.";
        return false;
      }
      if (this.selectedUnit === "minutes" && v < 3) {
        if (setError) this.durationError = "Minutes must be at least 3.";
        return false;
      }
      if (v > this.unitMax) {
        if (setError) this.durationError = `Maximum allowed is ${this.unitMax} ${this.selectedUnit}.`;
        return false;
      }

      this.durationError = "";
      return true;
    },

    formatExpiry() {
      const v = Number(this.durationValue);
      if (isNaN(v) || v < 0) return 0;

      switch (this.selectedUnit) {
        case "days": return v * 24 * 60 * 60;
        case "hours": return v * 60 * 60;
        case "minutes": return v * 60;
        default: return 0;
      }
    },

    toggleShowToken() {
      this.showToken = !this.showToken;
    },

    async onSave() {
      if (this.mode !== "access_token" && this.mode !== "refresh_token") return;
      if (!this.validateDuration(true)) return;

      this.apiError = "";
      this.loading = true;

      try {
        const appId = this.$route.params.id;
        const response = await apiInterceptor.post(
          environment("VUE_APP_APIGEE_API_URL") +
            `apps/${appId}/keys/${this.keyId}/access-token`,
          { expires_in: this.formatExpiry() }
        );

        useUIStore().updateToast({
          open: true,
          message: `${this.modeLabel} created successfully!`,
          type: "success",
        });

        this.generatedToken = response.access_token;
      } catch (err) {
        // Show inline — toasts render behind VDialog's native top-layer
        this.apiError = err?.response?.data?.detail || "Something went wrong!";
      } finally {
        this.loading = false;
      }
    },

    downloadToken() {
      const blob = new Blob([this.tokenToDisplay], { type: "text/plain" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = this.downloadFilename;
      a.click();
    },

    onCancel() {
      this.$emit("onCancel");
      if (this.generatedToken) this.$emit("fetchList");
    },
  },
};
</script>

<style scoped>
.modal-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}

.modal-description {
  font-size: 14px;
  color: var(--gray-04);
}

.warning-message {
  color: #bb4d00;
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #fee685;
  border-radius: 10px;
  background-color: #fffbeb;
  padding: 8px;
}

.field {
  margin-bottom: 8px;
}

.method-url-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.method-select {
  width: 160px;
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
}

.url-input {
  flex: 1;
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
}

.url-input.invalid {
  outline: 1px solid red;
  border-radius: 5px;
}

.error-text {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}

.expiry-disclaimer {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  line-height: 1.4;
  background-color: rgba(129, 56, 255, 0.25);
  padding: 10px;
  border-radius: 5px;
}

.token-display {
  width: 100%;
}

.token-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ccc;
  padding-right: 14px;
}

.token-input {
  flex: 1;
  background: transparent;
}

.token-icons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.token-icon {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  transition: color 0.2s ease;
}

.token-icon:hover {
  color: #000;
}

.api-error-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 5px;
  background: var(--vvd-color-alert-50, #fff3f3);
  color: var(--vvd-color-alert-700, #d32f2f);
  font-size: 13px;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1000;
  border-radius: 8px;
}

.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 4px 8px;
  position: absolute;
  z-index: 1001;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  font-size: 12px;
  white-space: nowrap;
}

.tooltip:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

@media (max-width: 480px) {
  .method-url-row {
    flex-direction: column;
    gap: 8px;
  }
  .method-select {
    width: 100%;
  }
  .token-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding-right: 0;
    border-bottom: none;
  }
  .token-icons {
    gap: 12px;
  }
}
</style>
